import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useCountries } from '@/application/queries'
import { useFilter } from '@/application/use-filter'
import { toCountryCodes } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  pageNum?: number,
  searchText?: string,
  countries?: string[],
  collections?: string[]
) {
  return [
    'findProducts',
    ecommerceIds,
    startDate,
    endDate,
    pageNum,
    searchText,
    countries,
    collections
  ]
}

export function useProducts(
  pageNum: number,
  searchText?: string,
  startDate?: string,
  endDate?: string
) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  const limit = 20
  async function callFindProducts() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.findProducts({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      offset: pageNum * limit,
      limit,
      searchText,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections
    })
  }

  const findProductsQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      pageNum,
      searchText,
      selectedCountries,
      selectedCollections
    ),
    queryFn: callFindProducts,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const products = findProductsQuery.data?.products ?? []
  const totalPages = Math.ceil(
    (findProductsQuery.data?.totalDistinctReturnedProducts ?? 0) / limit
  )

  return {
    products,
    totalPages,
    loading: findProductsQuery.isLoading || loadingCountries
  }
}
